/**
 * Janus credentials returned by `sip_session_start` / `session_start`.
 *
 * `JANUS_AUTH_MODE=token` (backend) sends a per-session stored token
 * (`janusToken`) and never the global API secret. The legacy mode sends
 * `janusApiSecret`.
 */
export interface JanusAuthPayload {
  janusAuthMode?: 'api_secret' | 'token' | string | null;
  janusToken?: string | null;
  janusApiSecret?: string | null;
}

export interface JanusAuthOptions {
  token?: string;
  apisecret?: string;
}

/**
 * Build the `token`/`apisecret` options for `new Janus(...)`.
 *
 * A token always wins; in token mode the API secret is never forwarded, even
 * if a stale payload still carries one.
 */
export function resolveJanusAuthOptions(payload: JanusAuthPayload | null | undefined): JanusAuthOptions {
  const token = String(payload?.janusToken || '').trim();
  if (token) {
    return { token };
  }
  if (payload?.janusAuthMode === 'token') {
    return {};
  }
  const apisecret = String(payload?.janusApiSecret || '').trim();
  return apisecret ? { apisecret } : {};
}

/** Non-sensitive auth mode label for debug logs and breadcrumbs. */
export function describeJanusAuth(payload: JanusAuthPayload | null | undefined): 'token' | 'api_secret' | 'none' {
  const options = resolveJanusAuthOptions(payload);
  if (options.token) {
    return 'token';
  }
  return options.apisecret ? 'api_secret' : 'none';
}
